import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { lancerPodium } from "@/shell/fete/moteur";

/**
 * 🏆 L'ENTRÉE SUR LE PODIUM de la semaine (Kayla, 09-10-2026 : « quand il
 * passe top 3, top 2 ou top 1, il faut une animation dingue »). Roulement de
 * tambour, projecteurs, la médaille qui tombe en tournant, et la couronne pour
 * le n°1 — tout le spectacle vit dans `fete/moteur.ts::lancerPodium`.
 * Déclenchée par `RangBadge` sur une montée vers une place 1 à 3 ; les autres
 * montées gardent la petite fête `RangMonte`.
 *
 * 🪟 Coque, 01-11-2026 : ce n'est PAS un `Classement.Podium` (qui range des
 * chatteurs sur le classement) mais une scène qui s'anime plein écran — la
 * médaille dessinée y est le spectacle, pas un indicateur. Elle reste donc au
 * moteur ; ses boutons et la prime (un `Badge`) viennent de la librairie, les
 * halos de néon en sont retirés.
 */
export interface FetePodium {
  id: number;
  rang: 1 | 2 | 3;
  /** Le chatteur juste derrière, celui qu'on vient de passer. */
  devant: string | null;
  /** La prime de la place, déjà formatée. */
  prime: string | null;
  /** Cette fenêtre a gagné le son (une seule sonne par personne). */
  sonIci: boolean;
}

export function PodiumCelebration({ fete, onFin, onOuvrir }: { fete: FetePodium | null; onFin: () => void; onOuvrir: () => void }) {
  const { t } = useTranslation();
  const racine = useRef<HTMLDivElement>(null);
  const rappels = useRef({ onFin, onOuvrir });
  rappels.current = { onFin, onOuvrir };

  useEffect(() => {
    if (!fete || !racine.current) return;
    return lancerPodium(
      racine.current,
      {
        rang: fete.rang,
        devant: fete.devant,
        prime: fete.prime,
        libelles: {
          surtitre: t("shell.rangPodium.surtitre", { defaultValue: "Classement de la semaine" }),
          devant: (name) => t("shell.rangPodium.devant", { name, defaultValue: "Tu passes devant {{name}}" }),
          prime: (montant) => t("shell.rangPodium.prime", { montant, defaultValue: "Prime de la place · {{montant}}" }),
          voir: t("shell.rangPodium.voir", { defaultValue: "Voir le classement" }),
          fermer: t("common.close", { defaultValue: "Fermer" }),
        },
      },
      fete.sonIci,
      { onOuvrir: () => rappels.current.onOuvrir(), onFermer: () => rappels.current.onFin() },
    );
  }, [fete, t]);

  if (!fete) return null;
  return createPortal(<div ref={racine} role="status" aria-live="polite" data-testid="rang-podium-fete" />, document.body);
}
